import {
  AssignmentLate as ActionRequiredIcon,
  List as AllIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ReceiptLong as SuratJalanIcon,
} from "@mui/icons-material";
import { Box, Collapse, IconButton, List, useTheme } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";

type TranslationKeys = keyof typeof translations.en;

interface SuratJalanMenuProps {
  onNavigate?: () => void;
}

const SuratJalanMenu: React.FC<SuratJalanMenuProps> = ({ onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const collapsedRef = useRef(false);
  const expandedRef = useRef(false);

  const isPage = useMemo(
    () =>
      location.pathname === "/surat-jalan" ||
      location.pathname === "/surat-jalan/action-required" ||
      location.pathname.startsWith("/surat-jalan/"),
    [location.pathname],
  );
  const isListPage = useMemo(
    () =>
      location.pathname === "/surat-jalan" || location.pathname === "/surat-jalan/action-required",
    [location.pathname],
  );
  const [expanded, setExpanded] = useState(() => isListPage);

  useEffect(() => {
    if (isListPage) {
      if (expandedRef.current) {
        setExpanded(true);
      } else if (collapsedRef.current) {
        setExpanded(false);
      } else {
        setExpanded(true);
      }
    } else if (!isPage) {
      setExpanded(false);
      expandedRef.current = false;
      collapsedRef.current = false;
    }
  }, [isListPage, isPage]);

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      expandedRef.current = next;
      collapsedRef.current = !next;
      return next;
    });
  }, []);

  const submenuItemSx = { pl: 4, py: 0.375, minHeight: "unset" } as const;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
        <SidebarMenuItem
          to="/surat-jalan/action-required"
          selected={isListPage}
          icon={<SuratJalanIcon />}
          text={t("Surat Jalan" as TranslationKeys)}
          sx={{ flex: 1 }}
          onNavigate={onNavigate}
          data-testid={TestId.SIDEBAR.MENU_ITEM_SURAT_JALAN}
        />
        <IconButton
          size="small"
          onClick={toggle}
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
            }),
            position: "absolute",
            right: 4,
            zIndex: 1,
            color: rawColor.white.transparent["07"],
            "&:hover": { backgroundColor: rawColor.white.transparent["12"] },
          }}
        >
          <ArrowDownIcon />
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding data-testid={TestId.SIDEBAR.SURAT_JALAN_MENU}>
          <SidebarMenuItem
            to="/surat-jalan/action-required"
            selected={location.pathname === "/surat-jalan/action-required"}
            icon={<ActionRequiredIcon fontSize="small" />}
            text={t("sidebar.actionRequired" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={submenuItemSx}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_SJ_ACTION_REQUIRED}
          />
          <SidebarMenuItem
            to="/surat-jalan"
            selected={location.pathname === "/surat-jalan"}
            icon={<AllIcon fontSize="small" />}
            text={t("sidebar.all" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={submenuItemSx}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_SJ_ALL}
          />
        </List>
      </Collapse>
    </Box>
  );
};

export default SuratJalanMenu;
